import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api, { formatApiError, fmtNum, fmtPrice, raceTime } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MatrixTable } from "@/components/race/MatrixTable";
import { ClassRuleDetail } from "@/components/race/ClassRuleDetail";
import { FinalRatingsTable } from "@/components/race/FinalRatingsTable";
import { RawDataEditor } from "@/components/race/RawDataEditor";
import { RaceFieldHeading, RaceFieldTable } from "@/components/race/RaceField";

const Stat = ({ label, value, testId }) => (
  <div className="px-5 py-3 border-r border-slate-800 last:border-r-0" data-testid={testId}>
    <div className="eyebrow">{label}</div>
    <div className="num text-white mt-1 text-sm">{value || "—"}</div>
  </div>
);

export default function RaceDetail() {
  const { id } = useParams();
  const fromResults = useLocation().state?.from === "results";
  const [race, setRace] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState(fromResults ? "final" : "field");

  useEffect(() => {
    Promise.all([api.get(`/races/${id}`), api.get("/settings")])
      .then(([r, s]) => { setRace(r.data); setSettings(s.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  if (!race || !settings) return <div className="py-20 grid place-items-center"><div className="loader-bar" /></div>;
  const ev = race.evaluation;
  const top = ev.ratings[0];

  return (
    <div className="space-y-6">
      <Link to={fromResults ? "/results" : "/program"} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors" data-testid="race-back-link">
        <ArrowLeft className="h-4 w-4" /> {fromResults ? "Winners & Places" : "Race Program"}
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <div className="eyebrow mb-2">{race.date || "Race"} • Race {race.race_number ?? "?"}{race.start_aest && <span className="text-amber-400" data-testid="race-start-aest"> • {raceTime(race)}</span>}</div>
          <h1 className="text-4xl sm:text-5xl font-bold" data-testid="race-title">{race.venue || "Unknown venue"} <span className="text-emerald-400">R{race.race_number ?? "?"}</span></h1>
          <p className="text-slate-400 mt-1">{race.race_name} {race.state && <span className="num text-xs text-slate-500">• {race.state} {race.start_time && `${race.start_time} local`}</span>}</p>
          {race.source_url && <a href={race.source_url} target="_blank" rel="noreferrer" data-testid="race-source-link" className="text-xs text-emerald-400 hover:text-emerald-300">View official fields ↗</a>}
        </div>
        {top && (
          <div className="panel px-5 py-3 text-right" data-testid="race-top-pick">
            <div className="eyebrow">Top Rated</div>
            <div className="font-display text-2xl font-bold">{top.name}</div>
            <div className="num text-sm"><span className="text-amber-400 font-bold">{fmtPrice(top.price)}</span> <span className="text-slate-500">• {fmtNum(top.pct, 1)}%</span></div>
          </div>
        )}
      </div>

      <div className="panel flex flex-wrap">
        <Stat label="Distance" value={race.distance && `${fmtNum(race.distance, 0)}m`} testId="race-stat-distance" />
        <Stat label="Class" value={race.race_class} testId="race-stat-class" />
        <Stat label="Track" value={race.track_condition} testId="race-stat-track" />
        <Stat label="Field" value={`${ev.field_size} runners`} testId="race-stat-field" />
        <Stat label="Prize" value={race.prize} testId="race-stat-prize" />
      </div>

      <div className="grid gap-6">
        <div className="panel p-5 min-w-0">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="scroll-x pb-1">
              <TabsList className="bg-[#0F172A] border border-slate-800 h-auto flex-wrap justify-start">
                {!fromResults && <TabsTrigger value="field" data-testid="matrix-tab-field">Field</TabsTrigger>}
                <TabsTrigger value="final" data-testid="matrix-tab-final">Final Ratings & $</TabsTrigger>
                {!fromResults && settings.matrices.map((m, i) => (
                  <TabsTrigger key={m.id} value={m.id} data-testid={`matrix-tab-${m.id}`}>
                    <span className="num text-[10px] text-slate-500 mr-1.5">M{i + 1}</span>{m.name}
                  </TabsTrigger>
                ))}
                {!fromResults && <TabsTrigger value="raw" data-testid="matrix-tab-raw">Raw Data</TabsTrigger>}
              </TabsList>
            </div>
            <TabsContent value="field" className="mt-5 space-y-5">
              <RaceFieldHeading race={race} prefix="race-field-heading" showClass />
              <RaceFieldTable horses={race.horses} prefix="race-field" todayScore={race.class_score?.score} />
            </TabsContent>
            <TabsContent value="final" className="mt-5">
              <FinalRatingsTable ratings={ev.ratings} matrices={settings.matrices} />
            </TabsContent>
            {settings.matrices.map((m) => (
              <TabsContent key={m.id} value={m.id} className="mt-5">
{m.id === "class_change" ? <ClassRuleDetail race={race} ratings={ev.ratings} /> : <MatrixTable matrix={m} ratings={ev.ratings} />}
              </TabsContent>
            ))}
            <TabsContent value="raw" className="mt-5">
              <RawDataEditor race={race} onSaved={(d) => { setRace(d); setTab("final"); }} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
